/**
 * Generic table layout: per-user column preference + fit-to-width.
 *
 * Extracted from Play Archive (`pages/archive/archive-layout.ts`, which now
 * re-exports these with its own column type) so every list page can share
 * the same behaviour:
 *
 *  - `ColumnPrefs` is the ordered, show/hide choice a user makes in the
 *    Columns picker. `reconcilePrefs` makes any stored choice total against
 *    the live column list (unknown ids dropped, new columns slotted in).
 *  - `fitColumns` NEVER scrolls sideways: it keeps the user's visible columns
 *    in order and drops the least important (highest `priority`) until the
 *    rest fit at their minimum widths; spare width is shared by `grow`. The
 *    dropped columns are reported so the table can show them in a row detail.
 */

export type Density = 'compact' | 'comfortable';

/** The fields `fitColumns` / prefs need from a column definition. */
export interface FitColumn {
  id: string;
  /** Lower = more important: kept first when the table is narrow. */
  priority: number;
  /** Narrowest the column may render, in CSS px. */
  minWidth: number;
  /** Widest its measured content may claim (defaults to 2 × minWidth). */
  maxWidth?: number;
  /** Share of spare width. */
  grow: number;
  defaultVisible: boolean;
  /** Always shown, never hideable or dropped. */
  isPinned?: boolean;
}

export interface ColumnPref {
  id: string;
  visible: boolean;
}

export interface ColumnPrefs {
  columns: ColumnPref[];
  density: Density;
}

export function defaultPrefs(columns: readonly FitColumn[]): ColumnPrefs {
  return {
    columns: columns.map((column) => ({ id: column.id, visible: column.defaultVisible })),
    density: 'compact',
  };
}

/** Stored preference → a total preference over the live registry. */
export function reconcilePrefs(
  stored: Partial<ColumnPrefs> | null | undefined,
  columns: readonly FitColumn[]
): ColumnPrefs {
  const defaults = defaultPrefs(columns);
  if (stored === null || stored === undefined || !Array.isArray(stored.columns)) return defaults;
  const known = new Map(columns.map((column) => [column.id, column]));
  const seen = new Set<string>();
  const ordered: ColumnPref[] = [];
  for (const entry of stored.columns) {
    if (typeof entry?.id !== 'string' || !known.has(entry.id) || seen.has(entry.id)) continue;
    seen.add(entry.id);
    const column = known.get(entry.id);
    ordered.push({
      id: entry.id,
      visible: column?.isPinned === true ? true : entry.visible === true,
    });
  }
  // New columns go right after their registry predecessor, so a new catalog
  // stat lands next to its siblings instead of at the far end.
  columns.forEach((column, index) => {
    if (seen.has(column.id)) return;
    seen.add(column.id);
    const pref = { id: column.id, visible: column.defaultVisible };
    const predecessor = index === 0 ? null : columns[index - 1]?.id;
    const at = predecessor === null ? -1 : ordered.findIndex((entry) => entry.id === predecessor);
    ordered.splice(at + 1, 0, pref);
  });
  return {
    columns: ordered,
    density: stored.density === 'comfortable' ? 'comfortable' : 'compact',
  };
}

export function toggleColumn(prefs: ColumnPrefs, id: string): ColumnPrefs {
  return {
    ...prefs,
    columns: prefs.columns.map((entry) =>
      entry.id === id ? { ...entry, visible: !entry.visible } : entry
    ),
  };
}

/** Move `id` by `delta` places (keyboard up/down). */
export function moveColumn(prefs: ColumnPrefs, id: string, delta: number): ColumnPrefs {
  const from = prefs.columns.findIndex((entry) => entry.id === id);
  if (from < 0) return prefs;
  const to = Math.max(0, Math.min(prefs.columns.length - 1, from + delta));
  return moveColumnTo(prefs, id, to);
}

/** Move `id` to index `to` (drag and drop). */
export function moveColumnTo(prefs: ColumnPrefs, id: string, to: number): ColumnPrefs {
  const from = prefs.columns.findIndex((entry) => entry.id === id);
  if (from < 0 || from === to) return prefs;
  const next = prefs.columns.slice();
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return prefs;
  next.splice(Math.max(0, Math.min(next.length, to)), 0, moved);
  return { ...prefs, columns: next };
}

/* -------------------------------------------------------------------- fit */

export interface FittedColumn<C extends FitColumn = FitColumn> {
  column: C;
  width: number;
}

export interface FitResult<C extends FitColumn = FitColumn> {
  /** Columns rendered, in the user's order, with pixel widths. */
  shown: FittedColumn<C>[];
  /** Visible-by-preference columns dropped for width (user order). */
  hidden: C[];
}

export interface FitOptions<C extends FitColumn = FitColumn> {
  /** Width available for the data columns (container − fixed chrome). */
  width: number;
  /** Extra px added to every column's minimum (0 by default). */
  cellPadding?: number;
  /** Multiplier on `minWidth` (the table scales type up on very wide screens). */
  scale?: number;
  /** Columns that carry nothing for the current view are skipped outright. */
  isApplicable?: (column: C) => boolean;
  /**
   * Measured content width per column id (already scaled, padding included).
   * Used instead of `minWidth` when present, capped at the column's
   * `maxWidth` so long prose still ellipsizes.
   */
  measured?: ReadonlyMap<string, number>;
}

/**
 * Keep the user's visible columns in order; while the minimum widths don't
 * fit, drop the visible column with the worst (highest) priority. Pinned
 * columns are never dropped. Spare width goes to columns by `grow`.
 */
export function fitColumns<C extends FitColumn>(
  columns: readonly C[],
  prefs: ColumnPrefs,
  options: FitOptions<C>
): FitResult<C> {
  const byId = new Map(columns.map((column) => [column.id, column]));
  const padding = options.cellPadding ?? 0;
  const scale = options.scale ?? 1;
  const isApplicable = options.isApplicable ?? (() => true);
  const min = (column: C): number => {
    const base = Math.ceil(column.minWidth * scale) + padding;
    const content = options.measured?.get(column.id);
    if (content === undefined) return base;
    const cap = Math.ceil((column.maxWidth ?? column.minWidth * 2) * scale) + padding;
    return Math.max(base, Math.min(Math.ceil(content), cap));
  };
  const wanted = prefs.columns
    .filter((entry) => entry.visible)
    .map((entry) => byId.get(entry.id))
    .filter((column): column is C => column !== undefined)
    .filter((column) => column.isPinned === true || isApplicable(column));

  const kept = new Set(wanted.map((column) => column.id));
  const need = (): number =>
    wanted.filter((column) => kept.has(column.id)).reduce((sum, column) => sum + min(column), 0);

  const dropOrder = wanted
    .filter((column) => column.isPinned !== true)
    .slice()
    .sort((left, right) => right.priority - left.priority);
  for (const column of dropOrder) {
    if (need() <= options.width) break;
    kept.delete(column.id);
  }

  const shownColumns = wanted.filter((column) => kept.has(column.id));
  const used = need();
  const spare = Math.max(0, options.width - used);
  const totalGrow = shownColumns.reduce((sum, column) => sum + column.grow, 0);
  const shown = shownColumns.map((column) => ({
    column,
    width: Math.floor(min(column) + (totalGrow === 0 ? 0 : (spare * column.grow) / totalGrow)),
  }));
  return { shown, hidden: wanted.filter((column) => !kept.has(column.id)) };
}
